import CheckIcon from "@mui/icons-material/Check";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Radio from "@mui/material/Radio";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import { visuallyHidden } from "@mui/utils";
import CondensedCell from "_shared/_views/tables/condensed-cell";
import SearchMatch from "_shared/_views/tables/search-match";
import { MRTNoMemo } from "_shared/_views/tables/table";
import { emptyRowsFallback } from "_shared/_views/tables/table-empty-state";
import { searchFieldWidth, searchTextFieldProps } from "_shared/_views/tables/table-toolbar";
import EntityMember from "_shared/entity-members/entity-member";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import Individual from "marketplace/individuals/individual";
import {
  createMRTColumnHelper,
  MRT_ColumnDef,
  MRT_ColumnFiltersState,
  MRT_RowSelectionState,
  MRT_SortingState,
  MRT_TableOptions,
} from "material-react-table";
import { ReactNode, useState } from "react";

/** Somebody a leader may hand their team over to: a colleague at the leader's firm. */
export type SuccessorCandidate = {
  member: EntityMember;
  individual?: Individual;
  name: string;
  email?: string;
  /**
   * Undefined when nobody could say: the reader could not read the colleague's profile. That is not
   * the same as lacking it, so such a colleague is never shown as lacking it (#6095); the server
   * decides when the team is offered.
   */
  hasSigningAuthority: boolean | undefined;
  /**
   * Whether the colleague has professional credentials on their profile, as far as leading the team
   * asks it. Somebody who joins a vendor team by leading it needs them (#5800); somebody already on
   * the team, or taking a client team over, is not asked.
   */
  credentials: SuccessorCredentials;
  onTheTeam: boolean;
};

export type SuccessorCredentials = "onTheirProfile" | "missing" | "notNeeded";

/**
 * Who would grant signing authority to a colleague who lacks it: the reader, the outgoing leader
 * when they approve a handover somebody else proposed, or nobody the reader can call on -- and then
 * the dialog says who can when such a colleague is chosen.
 */
export type SigningAuthorityGrant = "byYou" | "byTheOutgoingLeader" | "unavailable";

type SuccessorTableProps = {
  candidates: readonly SuccessorCandidate[];
  selectedUserId?: string;
  onSelect: (candidate: SuccessorCandidate) => void;
  /** Who grants signing authority to a colleague without it, which is what their row says. */
  signingAuthorityGrant: SigningAuthorityGrant;
  /** Show the credentials column: the team is a vendor team, whose members need them. */
  showCredentials?: boolean;
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
};

const columnHelper = createMRTColumnHelper<SuccessorCandidate>();

const WITHOUT_SIGNING_AUTHORITY: Record<SigningAuthorityGrant, string> = {
  byYou: "You can grant it",
  byTheOutgoingLeader: "Their leader grants it on approving",
  unavailable: "Ask whoever manages your firm's permissions",
};

const signingAuthorityFilterValue = (hasSigningAuthority: boolean | undefined) => {
  if (hasSigningAuthority === undefined) return "Not known";
  return hasSigningAuthority ? "Yes" : "No";
};

const CREDENTIALS: Record<SuccessorCredentials, string> = {
  onTheirProfile: "On their profile",
  missing: "Missing from their profile",
  notNeeded: "Not asked: already on the team",
};

/**
 * A colleague's credentials, in the warning color when they are missing: the one value here that
 * should catch a leader's eye. The cell and its fold under the name both draw it, so the warning
 * survives the fold on a phone.
 */
function CredentialsValue(props: Readonly<{ candidate: SuccessorCandidate; children?: ReactNode }>) {
  const { candidate } = props;
  return (
    <Box component="span" sx={{ color: candidate.credentials === "missing" ? "warning.main" : undefined }}>
      {props.children ?? CREDENTIALS[candidate.credentials]}
    </Box>
  );
}

export const SUCCESSOR_NAME_TEST_ID = "successor-name";

const teamOf = (candidate: SuccessorCandidate): string => (candidate.onTheTeam ? "On the team" : "Not on the team");

/**
 * The colleagues a team leader can hand the team over to (#5836): searchable, filterable by
 * signing authority, by credentials and by whether they are already on the team, and sortable by
 * any column. It opens on everybody (#6095), and anybody can be chosen: what a choice still needs
 * -- a grant of signing authority, or credentials -- the dialog says once it is made.
 */
export default function SuccessorTable(props: Readonly<SuccessorTableProps>) {
  const { candidates, selectedUserId, onSelect, signingAuthorityGrant } = props;
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);
  const [sorting, setSorting] = useState<MRT_SortingState>([{ id: "name", desc: false }]);
  const [globalFilter, setGlobalFilter] = useState("");
  const rowSelection: MRT_RowSelectionState = selectedUserId ? { [selectedUserId]: true } : {};
  // A phone has no width to spare for a column of radio buttons beside the avatars: the avatar is
  // the radio button there, and turns into a tick once its colleague is chosen.
  const theme = useTheme();
  const avatarChooses = useMediaQuery(theme.breakpoints.down("sm"));

  const signingAuthorityOf = (candidate: SuccessorCandidate) => {
    if (candidate.hasSigningAuthority === undefined) return "Not known to you";
    return candidate.hasSigningAuthority ? "Holds it" : WITHOUT_SIGNING_AUTHORITY[signingAuthorityGrant];
  };

  const columns: MRT_ColumnDef<SuccessorCandidate, string>[] = [
    // A column of its own, so the name beside it is the standard two-line cell that narrower columns
    // fold onto. On a phone the avatar is also the radio button.
    columnHelper.display({
      id: "avatar",
      header: "Photo",
      size: 56,
      enableResizing: false,
      Header: () => (
        <Box component="span" sx={visuallyHidden}>
          Photo
        </Box>
      ),
      Cell: ({ row }) =>
        avatarChooses ? (
          <Radio
            checked={row.getIsSelected()}
            onChange={() => onSelect(row.original)}
            onClick={(event) => event.stopPropagation()}
            icon={<IndividualAvatar individual={row.original.individual} />}
            checkedIcon={
              <Avatar sx={{ bgcolor: "primary.main" }}>
                <CheckIcon />
              </Avatar>
            }
            slotProps={{ input: { "aria-label": `Choose ${row.original.name}` } }}
            sx={{ p: 0 }}
          />
        ) : (
          <IndividualAvatar individual={row.original.individual} />
        ),
    }) as MRT_ColumnDef<SuccessorCandidate, string>,
    // The value carries the email after the name, so the search finds a colleague by either and
    // the column still sorts by name.
    columnHelper.accessor((candidate) => [candidate.name, candidate.email].filter(Boolean).join(" "), {
      id: "name",
      header: "Name",
      meta: { kind: "name" },
      Cell: ({ row, table }) => (
        <CondensedCell
          title={
            <span data-testid={SUCCESSOR_NAME_TEST_ID}>
              <SearchMatch table={table} text={row.original.name} />
            </span>
          }
          secondary={row.original.email && <SearchMatch table={table} text={row.original.email} />}
        />
      ),
    }),
    columnHelper.accessor((candidate): string => signingAuthorityFilterValue(candidate.hasSigningAuthority), {
      id: "signingAuthority",
      header: "Signing authority",
      // What decides the choice, so it is the last to fold under the name on a phone.
      meta: { kind: "text", fold: { into: "name", priority: 3, render: (candidate) => signingAuthorityOf(candidate) } },
      filterVariant: "select",
      filterSelectOptions: ["Yes", "No", "Not known"],
      // The search matches "Yes", "No" or "Not known", which the cell does not show, so it marks
      // nothing here: a mark in "Holds it" would claim a match the search never made (#6590).
      Cell: ({ row }) => signingAuthorityOf(row.original),
    }),
    ...(props.showCredentials
      ? [
          columnHelper.accessor((candidate): string => CREDENTIALS[candidate.credentials], {
            id: "credentials",
            header: "Credentials",
            meta: { kind: "text", fold: { into: "name", priority: 2, render: (candidate) => <CredentialsValue candidate={candidate} /> } },
            filterVariant: "select",
            filterSelectOptions: Object.values(CREDENTIALS),
            Cell: ({ row, renderedCellValue }) => <CredentialsValue candidate={row.original}>{renderedCellValue}</CredentialsValue>,
          }),
        ]
      : []),
    columnHelper.accessor(teamOf, {
      id: "team",
      header: "Team",
      meta: { kind: "status", fold: { into: "name", priority: 1, render: (candidate) => teamOf(candidate) } },
      filterVariant: "select",
      filterSelectOptions: ["On the team", "Not on the team"],
    }),
  ];

  const tableOptions: MRT_TableOptions<SuccessorCandidate> = {
    columns,
    data: [...candidates],
    // While the firm's roster loads, the table draws placeholder rows that carry no colleague: the
    // dialog crashed on them when the leader had nobody else on the team to show at once (#6094).
    getRowId: (candidate, index) => candidate.member?.userId.value ?? `skeleton-${index}`,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    enableSelectAll: false,
    positionToolbarAlertBanner: "none",
    enableColumnFilters: true,
    enableSorting: true,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: false,
    enableColumnActions: false,
    positionGlobalFilter: "left",
    // A plain match, as the other tables search: a fuzzy one keeps near misses such as
    // alan.2@firm beside alan@firm, so an exact email still leaves a choice to make.
    globalFilterFn: "includesString",
    // 25 a page, as every list opens (#6721): the dialog's content scrolls, so a firm of thirty no
    // longer pages through six pages of five to find somebody.
    initialState: { showGlobalFilter: true, showColumnFilters: true },
    // The dialog is too narrow for the rows-per-page picker beside the page range.
    muiPaginationProps: { showRowsPerPage: false },
    muiSearchTextFieldProps: ({ table }) => ({
      ...searchTextFieldProps({ searches: "your colleagues", table }),
      sx: searchFieldWidth,
    }),
    // The filters show from the start, and MRT holds each at 120px wide. The fit sizes a column from
    // its heading, so at 768px the Team column came out at 120px and its filter ran 6px past the
    // table's edge. Each filter takes its column's width instead (#6721).
    muiFilterTextFieldProps: { sx: { minWidth: 0 } },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => onSelect(row.original),
      sx: { cursor: "pointer" },
    }),
    onRowSelectionChange: (updater) => {
      const next = typeof updater === "function" ? updater(rowSelection) : updater;
      const chosenUserId = Object.keys(next).find((userId) => next[userId]);
      const chosen = candidates.find((candidate) => candidate.member.userId.value === chosenUserId);
      if (chosen) onSelect(chosen);
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    renderEmptyRowsFallback: emptyRowsFallback({
      isError: props.isError ?? false,
      isLoading: props.isLoading ?? false,
      onRetry: () => props.onRetry?.(),
      subject: "colleagues",
    }),
    state: {
      columnFilters,
      globalFilter,
      sorting,
      rowSelection,
      columnVisibility: { "mrt-row-select": !avatarChooses },
      isLoading: props.isLoading ?? false,
    },
  };

  return <MRTNoMemo<SuccessorCandidate> options={tableOptions} />;
}
